import React, { useEffect } from "react";
import {
  motion,
  useMotionValue,
  useTransform,
  animate,
} from "framer-motion";
import { ResponsiveContainer, AreaChart, Area } from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";

export function AnimatedNumber({ value, decimals = 0, prefix = "", suffix = "" }) {
  const motionValue = useMotionValue(0);
  const display = useTransform(motionValue, (v) => {
    const n = decimals === 0 ? Math.round(v) : Number(v.toFixed(decimals));
    return `${prefix}${n.toLocaleString()}${suffix}`;
  });
  useEffect(() => {
    const controls = animate(motionValue, Number(value) || 0, {
      duration: 1.1,
      ease: "easeOut",
    });
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <motion.span>{display}</motion.span>;
}

export default function StatCard({
  icon: Icon,
  label,
  value,
  accent = "#1E3A8A",
  trend = 0,
  spark = [],
  delay = 0,
  isText = false,
}) {
  const trendUp = trend >= 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ y: -3, transition: { duration: 0.2 } }}
      className="relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl shadow-sm hover:shadow-md transition-shadow p-5"
    >
      <div
        className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-15"
        style={{ background: `radial-gradient(circle, ${accent} 0%, transparent 70%)` }}
      />
      <div className="relative flex items-start justify-between">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shadow-sm"
          style={{
            background: `linear-gradient(135deg, ${accent} 0%, ${accent}D9 100%)`,
          }}
        >
          <Icon className="w-5 h-5 text-white" />
        </div>
        {trend !== null && trend !== undefined && trend !== 0 && (
          <div
            className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
              trendUp
                ? "bg-emerald-50 text-emerald-700"
                : "bg-red-50 text-red-700"
            }`}
          >
            {trendUp ? (
              <TrendingUp className="w-3 h-3" />
            ) : (
              <TrendingDown className="w-3 h-3" />
            )}
            {Math.abs(trend)}%
          </div>
        )}
      </div>

      <div className="relative mt-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          {label}
        </div>
        <div className="mt-1 text-3xl font-bold text-slate-900 capitalize font-display">
          {isText ? (
            value || "—"
          ) : (
            <AnimatedNumber value={value || 0} />
          )}
        </div>
      </div>

      {spark && spark.length > 1 && (
        <div className="relative -mx-2 -mb-2 mt-3 h-12">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={spark.map((v, i) => ({ x: i, y: v }))}>
              <defs>
                <linearGradient id={`spark-${label}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="y"
                stroke={accent}
                strokeWidth={2}
                fill={`url(#spark-${label})`}
                isAnimationActive
                animationDuration={900}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </motion.div>
  );
}
